/* ============================================================================
   Collection grid (sections/kc-collection-grid.liquid): <kc-collection>.
   The search results grid (sections/kc-search-grid.liquid) uses it too;
   its forms carry the search words, so every address keeps the search.

   1. Infinite scroll. When the "Show more" button comes within reach, the
      next page is fetched through the Section Rendering API and its cards
      are appended. The button is a plain link to ?page=N underneath; a
      click loads the page too and moves focus to its first card. After
      "Stop loading on its own after" pages, only the button loads more.
      A page can come back with no cards (sold-out products are skipped),
      so the watch is re-armed after every load and carries on while the
      button is still in reach. A failed request stops the automatic
      loading until the button is clicked.

   2. Back button. How many pages were loaded and the scroll position are
      kept in sessionStorage. Coming back to the same address (say, from a
      product page) loads those pages again and scrolls back, unless the
      browser restored the whole page from its back/forward cache anyway.

   3. Sort, price chips, "Clear all" and the filter drawer re-render only
      this section and push the new address, so refresh, sharing and the
      back button keep the choices. While the drawer is open, the grid
      behind it updates as boxes are ticked (prices after a short pause);
      the drawer keeps its own inputs and only refreshes its counts and
      button label. If a request fails, the page simply loads normally.

   Without JavaScript the links, the sort form and the "Show more" link
   still work as plain page loads.
   ============================================================================ */
(() => {
  if (customElements.get('kc-collection')) return;

  const stillMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STORE_KEY = 'kc-collection';
  const STORE_TTL = 30 * 60 * 1000;
  const DRAWER_OUT = 260;
  const PRICE_PAUSE = 600;

  // While the template is previewed (?view=kawaii), every request and
  // address keeps the preview on.
  function keepView(url) {
    const view = new URLSearchParams(window.location.search).get('view');
    if (view && !url.searchParams.has('view')) url.searchParams.set('view', view);
    return url;
  }

  // The address a listing lives at: no page number, no section id.
  function listingUrl(href) {
    const url = new URL(href, window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return keepView(url);
  }

  function formUrl(form) {
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.href);
    url.search = '';
    new FormData(form).forEach((value, name) => {
      if (String(value).trim() !== '') url.searchParams.append(name, value);
    });
    return listingUrl(url.toString());
  }

  function modified(event) {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function readSaved() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  class KcCollection extends HTMLElement {
    connectedCallback() {
      const first = !this.ready;
      if (first) {
        this.ready = true;
        this.sectionId = this.dataset.section;
        this.auto = this.dataset.auto === 'true';
        this.autoPages = Number(this.dataset.autoPages) || 0;
        this.pages = 1;
        this.autoLoaded = 0;
        this.loading = null;
        this.failed = false;
        this.restoring = false;
        this.current = listingUrl(window.location.href).toString();

        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('submit', (event) => this.onSubmit(event));
        this.onPopBound = () => this.onPop();
        this.onHideBound = () => this.save();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.autoLoad();
            },
            { rootMargin: '0px 0px 900px 0px' }
          );
        }
        this.bindDrawer();
      }

      window.addEventListener('popstate', this.onPopBound);
      window.addEventListener('pagehide', this.onHideBound);
      this.watch();
      if (first) this.restore();
    }

    // The theme editor swaps the section out when its settings change.
    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopBound);
      window.removeEventListener('pagehide', this.onHideBound);
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      window.clearTimeout(this.filterTimer);
      window.clearTimeout(this.shutTimer);
      if (this.drawer && this.drawer.open) this.lock(false);
    }

    get grid() {
      return this.querySelector('[data-kc-grid]');
    }

    get nextLink() {
      return this.querySelector('[data-kc-next]');
    }

    get drawer() {
      return this.querySelector('[data-kc-drawer]');
    }

    /* --- 1. infinite scroll ------------------------------------------- */

    // (Re)arms the watch on the "Show more" button. Observing starts with
    // a report of where the button is now, so a page that came back short
    // leads straight to the next one.
    watch() {
      if (!this.observer) return;
      this.observer.disconnect();
      const link = this.nextLink;
      if (link && this.auto) this.observer.observe(link);
    }

    autoLoad() {
      if (this.restoring || this.failed) return;
      if (this.autoPages && this.autoLoaded >= this.autoPages) return;
      this.loadMore({ auto: true });
    }

    loadMore({ auto = false, focus = false } = {}) {
      if (this.loading) return this.loading;
      const link = this.nextLink;
      if (!link) return Promise.resolve(false);

      this.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');

      this.loading = this.fetchSection(link.href)
        .then((doc) => {
          const incoming = doc.querySelector('[data-kc-grid]');
          const grid = this.grid;
          if (!incoming || !grid) throw new Error('kc-collection: no product grid in the response');

          const cards = Array.from(incoming.querySelectorAll(':scope > [data-kc-card]'));
          grid.append(...cards);

          const more = this.querySelector('[data-kc-more]');
          const freshMore = doc.querySelector('[data-kc-more]');
          if (more && freshMore) more.replaceWith(freshMore);
          else if (more) more.replaceChildren();

          this.pages += 1;
          if (auto) this.autoLoaded += 1;
          this.failed = false;
          this.count();

          const seen = this.querySelector('.kc-cmore__seen');
          this.announce(seen ? seen.textContent : '');

          if (focus && cards.length) {
            const title = cards[0].querySelector('.kc-card__title');
            if (title) title.focus();
          }
          this.save();
          return true;
        })
        .catch((error) => {
          console.error(error);
          this.failed = true;
          return false;
        })
        .finally(() => {
          this.loading = null;
          this.classList.remove('is-loading');
          const next = this.nextLink;
          if (next) next.removeAttribute('aria-disabled');
          this.watch();
        });

      return this.loading;
    }

    // "You've seen [shown] of [total]": the server only knows its own page.
    count() {
      const grid = this.grid;
      const n = grid ? grid.querySelectorAll('[data-kc-card]').length : 0;
      this.querySelectorAll('[data-kc-shown]').forEach((el) => {
        el.textContent = n.toLocaleString('en-US');
      });
      return n;
    }

    announce(text) {
      const status = this.querySelector('[data-kc-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = text.replace(/\s+/g, ' ').trim();
      }, 60);
    }

    fetchSection(href, signal) {
      const url = keepView(new URL(href, window.location.href));
      url.searchParams.set('section_id', this.sectionId);
      return fetch(url.toString(), { signal, headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(`kc-collection: HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'));
    }

    /* --- 2. back button ------------------------------------------------ */

    save() {
      try {
        const key = window.location.pathname + window.location.search;
        if (this.pages < 2) {
          const saved = readSaved();
          if (saved && saved.key === key) window.sessionStorage.removeItem(STORE_KEY);
          return;
        }
        window.sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({ key, pages: this.pages, y: Math.round(window.scrollY), at: Date.now() })
        );
      } catch (e) {
        /* storage full or blocked: nothing to restore later */
      }
    }

    restore() {
      const saved = readSaved();
      const key = window.location.pathname + window.location.search;
      if (!saved || saved.key !== key || saved.pages < 2 || Date.now() - saved.at > STORE_TTL) return;

      const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      if (!nav || nav.type !== 'back_forward') return;

      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      this.restoring = true;

      const step = () => {
        if (this.pages >= saved.pages || !this.nextLink) return Promise.resolve();
        return this.loadMore().then((ok) => (ok ? step() : undefined));
      };

      step().then(() => {
        this.restoring = false;
        window.scrollTo({ top: saved.y, behavior: 'instant' });
        this.watch();
      });
    }

    /* --- 3. sort, chips, filters ------------------------------------- */

    render(href, { push = true, fromDrawer = false } = {}) {
      const url = listingUrl(href);
      const target = url.toString();

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      const active = document.activeElement;
      const focusKey = active && this.contains(active) ? active.dataset.kcKey : null;

      this.classList.add('is-busy');
      this.rendering = this.fetchSection(target, signal)
        .then((doc) => {
          const fresh = doc.querySelector('[data-kc-results]');
          const results = this.querySelector('[data-kc-results]');
          if (!fresh || !results) throw new Error('kc-collection: no results in the response');

          results.replaceChildren(...fresh.childNodes);
          this.syncDrawer(doc, fromDrawer);

          if (push) window.history.pushState({ kcCollection: true }, '', url.pathname + url.search);
          this.current = target;
          this.pages = 1;
          this.autoLoaded = 0;
          this.failed = false;
          this.count();
          this.watch();
          this.save();

          const count = this.querySelector('.kc-cbar__count');
          this.announce(count ? count.textContent : '');

          if (focusKey) {
            const again = this.querySelector(`[data-kc-key="${CSS.escape(focusKey)}"]`);
            if (again) again.focus({ preventScroll: true });
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          window.location.assign(target);
        })
        .finally(() => {
          if (signal.aborted) return;
          this.classList.remove('is-busy');
          this.rendering = null;
        });

      return this.rendering;
    }

    // While the drawer is open it keeps the shopper's own ticks; only the
    // counts, the disabled states and the button label follow the results.
    syncDrawer(doc, keep) {
      const drawer = this.drawer;
      const fresh = doc.querySelector('[data-kc-drawer]');
      if (!drawer || !fresh) return;

      if (keep && drawer.open) {
        drawer.querySelectorAll('[data-kc-sync][id]').forEach((el) => {
          const twin = fresh.querySelector(`#${CSS.escape(el.id)}`);
          if (!twin) return;
          if (el instanceof HTMLInputElement) el.disabled = twin.disabled && !el.checked;
          else el.textContent = twin.textContent;
        });
        return;
      }
      drawer.replaceChildren(...fresh.childNodes);
    }

    applyFilters() {
      window.clearTimeout(this.filterTimer);
      this.filterTimer = null;
      const form = this.querySelector('[data-kc-filters]');
      if (!form) return Promise.resolve();
      const target = formUrl(form).toString();
      if (target === this.current) return this.rendering || Promise.resolve();
      return this.render(target, { fromDrawer: true });
    }

    queueFilters(delay) {
      window.clearTimeout(this.filterTimer);
      this.filterTimer = window.setTimeout(() => this.applyFilters(), delay);
    }

    onPop() {
      const target = listingUrl(window.location.href).toString();
      if (target === this.current) return;
      this.render(target, { push: false });
    }

    /* --- the drawer ---------------------------------------------------- */

    bindDrawer() {
      const drawer = this.drawer;
      if (!drawer || drawer.dataset.kcBound) return;
      drawer.dataset.kcBound = 'true';

      // Escape slides it out like the close button does.
      drawer.addEventListener('cancel', (event) => {
        if (stillMotion.matches) return;
        event.preventDefault();
        this.closeDrawer();
      });

      drawer.addEventListener('close', () => {
        window.clearTimeout(this.shutTimer);
        drawer.classList.remove('is-closing');
        this.lock(false);
        const opener = this.querySelector('[data-kc-open]');
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          // The button may have been re-rendered while the drawer was open,
          // leaving the browser nothing to hand focus back to.
          const focused = document.activeElement;
          if (!focused || focused === document.body || !focused.isConnected || drawer.contains(focused)) {
            opener.focus();
          }
        }
      });
    }

    openDrawer() {
      const drawer = this.drawer;
      if (!drawer || drawer.open || typeof drawer.showModal !== 'function') return;
      window.clearTimeout(this.shutTimer);
      drawer.classList.remove('is-closing');
      drawer.showModal();
      this.lock(true);
      const opener = this.querySelector('[data-kc-open]');
      if (opener) opener.setAttribute('aria-expanded', 'true');
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer || !drawer.open) return;
      if (stillMotion.matches) {
        drawer.close();
        return;
      }
      if (drawer.classList.contains('is-closing')) return;
      drawer.classList.add('is-closing');
      window.clearTimeout(this.shutTimer);
      this.shutTimer = window.setTimeout(() => drawer.close(), DRAWER_OUT);
    }

    // The theme's own scroll lock (body.overflow-hidden, padded by the
    // scrollbar's width), as the header's dialogs use.
    lock(on) {
      if (on) {
        if (typeof window.setScrollbarWidth === 'function') window.setScrollbarWidth();
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }

    /* --- events -------------------------------------------------------- */

    onClick(event) {
      const drawer = this.drawer;
      // A click on the dimmed page beside the panel lands on the dialog.
      if (drawer && event.target === drawer) {
        this.closeDrawer();
        return;
      }

      const el = event.target.closest('[data-kc-next], [data-kc-link], [data-kc-open], [data-kc-close]');
      if (!el || !this.contains(el)) return;

      if (el.hasAttribute('data-kc-next')) {
        if (modified(event)) return;
        event.preventDefault();
        if (el.getAttribute('aria-disabled') === 'true') return;
        this.failed = false;
        this.loadMore({ focus: true });
      } else if (el.hasAttribute('data-kc-link')) {
        if (modified(event)) return;
        event.preventDefault();
        this.render(el.href);
      } else if (el.hasAttribute('data-kc-open')) {
        this.openDrawer();
      } else if (el.hasAttribute('data-kc-close')) {
        this.closeDrawer();
      }
    }

    onChange(event) {
      const el = event.target;
      if (el.matches('[data-kc-sort]')) {
        const url = listingUrl(window.location.href);
        url.searchParams.set('sort_by', el.value);
        this.render(url.toString());
      } else if (el.form && el.form.matches('[data-kc-filters]')) {
        this.queueFilters(0);
      }
    }

    onInput(event) {
      const el = event.target;
      if (el.type === 'number' && el.form && el.form.matches('[data-kc-filters]')) {
        this.queueFilters(PRICE_PAUSE);
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-kc-filters]')) {
        event.preventDefault();
        if (this.filterTimer) this.applyFilters();
        this.closeDrawer();
      } else if (form.matches('.kc-cbar__sort')) {
        event.preventDefault();
        const select = form.querySelector('[data-kc-sort]');
        if (select) select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  customElements.define('kc-collection', KcCollection);
})();
